import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFormatString } from './utils';
import { getDateFormatterForLanguage } from '@shared/hooks/useFormattedDate/dateFormatter';

export type LocaleContextType = Readonly<{
  language: string;
  dateFormat: string;
  dateInputMask: string;
  formatDate: ReturnType<typeof getDateFormatterForLanguage>;
}>;

export const LocaleContext = React.createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider: React.FC = ({ children }) => {
  const { i18n } = useTranslation();

  const language = i18n.language;

  const value = React.useMemo<LocaleContextType>(() => {
    return {
      language,
      ...getDateFormatString(language),
      formatDate: getDateFormatterForLanguage(language),
    };
  }, [language]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocaleContext = () => {
  const context = React.useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocaleContext() can only be used within a LocaleProvider.');
  }
  return context;
};

/**
 * Returns the locale/language for the event.
 * Currently hardcoded to 'en-US', but will be replaced with event-level locale setting in the future.
 * @returns Object with language property containing the BCP 47 language tag (e.g., 'en-US', 'es-ES', 'fr-FR')
 */
export const useEventLocale = (): { language: string } => {
  // TODO: Query event locale from GraphQL when backend support is added
  return { language: 'en-US' };
};
